import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Stack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Switch,
  Button,
  Divider,
  Alert,
  Select,
  Option,
  Slider,
  Sheet,
  Grid,
  Chip,
  Tabs,
  TabList,
  Tab,
  TabPanel,
} from '@mui/joy';
import { FaUpload, FaEye, FaSave, FaUndo, FaCode, FaEdit } from 'react-icons/fa';
import { IconWrapper } from '../../../components/IconWrapper';
import { Editor as TinyMCEEditor } from '@tinymce/tinymce-react';
import { pdfExportConfigService } from '../services/pdfExportConfig.service';
import { documentTemplateService } from '../services/documentTemplate.service';
import { documentsService } from '../services/documents.service';
import type { PdfExportConfig, PdfExportConfigInput } from '../types/pdfExportConfig.types';
import type { TemplateKind } from '../types/documents.types';
import { normalizeDescriptionTemplateMarkup } from '../utils/richTextDescription';
import { KSU_INVOICE_TEMPLATE } from '../../accounts/ksuInvoiceTemplate';

export const PdfExportConfigPanel: React.FC = () => {
  const [configs, setConfigs] = useState<PdfExportConfig[]>([]);
  const [organisations, setOrganisations] = useState<Array<{ id: string; name: string; code: string }>>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [selectedDocType, setSelectedDocType] = useState<TemplateKind>('Quotation');
  const [currentConfig, setCurrentConfig] = useState<PdfExportConfig | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  // Form state
  const [formData, setFormData] = useState<PdfExportConfigInput>({
    organisationId: '',
    documentType: 'Quotation',
    companyName: 'Keystone Glazing',
    companyTradingName: '',
    companyUtr: '',
    companyAddress: '',
    companyPhone: '',
    companyEmail: '',
    companyVatNumber: '',
    bankName: '',
    bankAccountName: '',
    bankAccountNumber: '',
    bankSortCode: '',
    bankIban: '',
    bankSwift: '',
    companyRegistrationNumber: '',
    logoPath: '',
    logoWidth: 200,
    logoHeight: 100,
    headerImagePath: '',
    headerTitle: 'Professional Glazing Services',
    headerSubtitle: '',
    headerColor: '#1976d2',
    footerImagePath: '',
    footerText: 'Thank you for your business',
    showGenerationDate: true,
    showPageNumbers: true,
    primaryColor: '#1976d2',
    secondaryColor: '#666666',
    fontFamily: 'Arial, sans-serif',
    fontSizeBase: 14,
    businessRegisteredAddress: '',
    businessWebsite: '',
    businessTerms: '',
  });

  // Template content state - these are the actual document templates
  const [templateContent, setTemplateContent] = useState<Record<TemplateKind, string>>({
    Quotation: `<div class="document-template">
      <!-- Header Section -->
      <div class="header">
        <div class="header-content">
          <div class="title">{{headerTitle || 'Quote'}}</div>
          <div class="subtitle">{{documentType}} - {{documentRef}}</div>
          <div class="date">Date: {{documentDate}}</div>
        </div>
        <div class="logo-section">
          {{#logoPath}}
          <img src="{{logoPath}}" alt="Company Logo" class="logo" />
          {{/logoPath}}
        </div>
      </div>

      <!-- Company Information -->
      <div class="info-grid">
        <div class="info-section">
          <h3>From</h3>
          <p>{{companyName}}<br />
          {{#companyAddress}}{{companyAddress}}<br />{{/companyAddress}}
          {{#companyPhone}}Phone: {{companyPhone}}<br />{{/companyPhone}}
          {{#companyEmail}}Email: {{companyEmail}}<br />{{/companyEmail}}
          {{#companyVatNumber}}VAT: {{companyVatNumber}}{{/companyVatNumber}}</p>
        </div>
        <div class="info-section">
          <h3>To</h3>
          <p>{{customerName}}<br />
          {{#customerAddress}}{{customerAddress}}<br />{{/customerAddress}}
          {{#customerEmail}}Email: {{customerEmail}}<br />{{/customerEmail}}
          {{#customerPhone}}Phone: {{customerPhone}}{{/customerPhone}}</p>
        </div>
      </div>

      <!-- Document Description -->
      {{#description}}
      <div class="description-section">
        <h3>Description</h3>
        {{description}}
      </div>
      {{/description}}

      <!-- Line Items Table -->
      {{#hasLineItems}}
      <table class="items-table">
        <thead>
          <tr>
            <th>Item</th>
            <th>Qty</th>
            <th>Unit</th>
            <th>Unit Price</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {{#lineItems}}
          <tr>
            <td><strong>{{name}}</strong><br />
            {{#description}}<span class="item-description">{{description}}</span>{{/description}}</td>
            <td>{{quantity}}</td>
            <td>{{unit}}</td>
            <td>{{unitPrice}}</td>
            <td>{{lineTotal}}</td>
          </tr>
          {{/lineItems}}
        </tbody>
        <tfoot>
          <tr>
            <td colspan="4">Base Cost Total</td>
            <td>{{baseCostTotal}}</td>
          </tr>
          {{#showFees}}
          <tr>
            <td colspan="4">Admin Fee ({{adminPct}}%)</td>
            <td>{{adminFee}}</td>
          </tr>
          <tr>
            <td colspan="4">Contingency ({{contingencyPct}}%)</td>
            <td>{{contingency}}</td>
          </tr>
          <tr>
            <td colspan="4">Profit Margin ({{profitPct}}%)</td>
            <td>{{profit}}</td>
          </tr>
          {{/showFees}}
          {{#vatEnabled}}
          <tr>
            <td colspan="4">VAT ({{vatPct}}%)</td>
            <td>{{vatAmount}}</td>
          </tr>
          {{/vatEnabled}}
          <tr class="total-row">
            <td colspan="4"><strong>Total{{#vatEnabled}} (incl. VAT){{/vatEnabled}}</strong></td>
            <td><strong>{{grandTotal}}</strong></td>
          </tr>
        </tfoot>
      </table>
      {{/hasLineItems}}
    </div>`,
    Estimate: `<div class="document-template">
      <!-- Header Section -->
      <div class="header">
        <div class="header-content">
          <div class="title">{{headerTitle || 'Estimate'}}</div>
          <div class="subtitle">{{documentType}} - {{documentRef}}</div>
          <div class="date">Date: {{documentDate}}</div>
        </div>
        <div class="logo-section">
          {{#logoPath}}
          <img src="{{logoPath}}" alt="Company Logo" class="logo" />
          {{/logoPath}}
        </div>
      </div>

      <!-- Company Information -->
      <div class="info-grid">
        <div class="info-section">
          <h3>From</h3>
          <p>{{companyName}}<br />
          {{#companyAddress}}{{companyAddress}}<br />{{/companyAddress}}
          {{#companyPhone}}Phone: {{companyPhone}}<br />{{/companyPhone}}
          {{#companyEmail}}Email: {{companyEmail}}<br />{{/companyEmail}}
          {{#companyVatNumber}}VAT: {{companyVatNumber}}{{/companyVatNumber}}</p>
        </div>
        <div class="info-section">
          <h3>To</h3>
          <p>{{customerName}}<br />
          {{#customerAddress}}{{customerAddress}}<br />{{/customerAddress}}
          {{#customerEmail}}Email: {{customerEmail}}<br />{{/customerEmail}}
          {{#customerPhone}}Phone: {{customerPhone}}{{/customerPhone}}</p>
        </div>
      </div>

      <!-- Document Description -->
      {{#description}}
      <div class="description-section">
        <h3>Description</h3>
        {{description}}
      </div>
      {{/description}}

      <!-- Line Items Table -->
      {{#hasLineItems}}
      <table class="items-table">
        <thead>
          <tr>
            <th>Item</th>
            <th>Qty</th>
            <th>Unit</th>
            <th>Unit Price</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {{#lineItems}}
          <tr>
            <td><strong>{{name}}</strong><br />
            {{#description}}<span class="item-description">{{description}}</span>{{/description}}</td>
            <td>{{quantity}}</td>
            <td>{{unit}}</td>
            <td>{{unitPrice}}</td>
            <td>{{lineTotal}}</td>
          </tr>
          {{/lineItems}}
        </tbody>
        <tfoot>
          <tr>
            <td colspan="4">Base Cost Total</td>
            <td>{{baseCostTotal}}</td>
          </tr>
          {{#showFees}}
          <tr>
            <td colspan="4">Admin Fee ({{adminPct}}%)</td>
            <td>{{adminFee}}</td>
          </tr>
          <tr>
            <td colspan="4">Contingency ({{contingencyPct}}%)</td>
            <td>{{contingency}}</td>
          </tr>
          <tr>
            <td colspan="4">Profit Margin ({{profitPct}}%)</td>
            <td>{{profit}}</td>
          </tr>
          {{/showFees}}
          {{#vatEnabled}}
          <tr>
            <td colspan="4">VAT ({{vatPct}}%)</td>
            <td>{{vatAmount}}</td>
          </tr>
          {{/vatEnabled}}
          <tr class="total-row">
            <td colspan="4"><strong>Total{{#vatEnabled}} (incl. VAT){{/vatEnabled}}</strong></td>
            <td><strong>{{grandTotal}}</strong></td>
          </tr>
        </tfoot>
      </table>
      {{/hasLineItems}}
    </div>`,
    Invoice: `<div class="document-template">
      <!-- Header -->
      <div class="header">
        <div class="header-content">
          <div class="title">{{headerTitle || 'Invoice'}}</div>
          <div class="subtitle">
            {{documentType}} - {{documentRef}}
            {{#invoiceNumber}} • Invoice No: {{invoiceNumber}}{{/invoiceNumber}}
          </div>
          <div class="date">Date: {{documentDate}}</div>
          {{#dueDate}}<div class="date">Payment terms: {{paymentTermsLabel}} • Due date: {{dueDate}}</div>{{/dueDate}}
          {{#paymentTermsWording}}<div class="payment-terms">{{paymentTermsWording}}</div>{{/paymentTermsWording}}
        </div>
        <div class="logo-section">
          {{#logoPath}}
          <img src="{{logoPath}}" alt="Company Logo" class="logo" />
          {{/logoPath}}
        </div>
      </div>

      <!-- Parties -->
      <div class="info-grid">
        <div class="info-section">
          <h3>From</h3>
          <p>{{companyName}}<br />
          {{#companyAddress}}{{companyAddress}}<br />{{/companyAddress}}
          {{#companyPhone}}Phone: {{companyPhone}}<br />{{/companyPhone}}
          {{#companyEmail}}Email: {{companyEmail}}<br />{{/companyEmail}}
          {{#companyVatNumber}}VAT: {{companyVatNumber}}{{/companyVatNumber}}</p>
        </div>
        <div class="info-section">
          <h3>Bill To</h3>
          <p>{{customerName}}<br />
          {{#customerAddress}}{{customerAddress}}<br />{{/customerAddress}}
          {{#customerEmail}}Email: {{customerEmail}}<br />{{/customerEmail}}
          {{#customerPhone}}Phone: {{customerPhone}}{{/customerPhone}}</p>
        </div>
      </div>

      <!-- Description -->
      {{#description}}
      <div class="description-section">
        <h3>Description</h3>
        {{description}}
      </div>
      {{/description}}

      <!-- Items -->
      {{#hasLineItems}}
      <table class="items-table">
        <thead>
          <tr>
            <th>Item</th>
            <th>Qty</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {{#lineItems}}
          <tr>
            <td>
              <strong>{{name}}</strong>
              {{#description}}<br /><span class="item-description">{{description}}</span>{{/description}}
            </td>
            <td>{{quantity}}</td>
            <td>{{lineTotal}}</td>
          </tr>
          {{/lineItems}}
        </tbody>
        <tfoot>
          <tr>
            <td colspan="2">Subtotal</td>
            <td>{{baseCostTotal}}</td>
          </tr>
          {{#discountTotal}}
          <tr>
            <td colspan="2">Discounts</td>
            <td>-{{discountTotal}}</td>
          </tr>
          {{/discountTotal}}
          {{#shippingTotal}}
          <tr>
            <td colspan="2">Delivery/Shipping</td>
            <td>{{shippingTotal}}</td>
          </tr>
          {{/shippingTotal}}

          <!-- VAT summary -->
          {{#vatEnabled}}
            {{#vatBreakdown}}
            <tr>
              <td colspan="2">VAT ({{rate}}%)</td>
              <td>{{amount}}</td>
            </tr>
            {{/vatBreakdown}}
            {{^vatBreakdown}}
            <tr>
              <td colspan="2">VAT ({{vatPct}}%)</td>
              <td>{{vatAmount}}</td>
            </tr>
            {{/vatBreakdown}}
          {{/vatEnabled}}

          <tr class="total-row">
            <td colspan="2">
              <strong>Total{{#vatEnabled}} (incl. VAT){{/vatEnabled}}</strong>
            </td>
            <td><strong>{{grandTotal}}</strong></td>
          </tr>

          <!-- Deposit breakdown: quotes and legacy combined invoices only.
               Dedicated deposit invoices omit this row — the line item is the deposit. -->
          {{^isDepositInvoice}}
          {{#hasDeposit}}
            {{#depositAppliedToInvoice}}
            <tr>
              <td colspan="2">
                Less: Deposit received{{#depositPercentage}} ({{depositPercentage}}%){{/depositPercentage}}
                {{#depositReference}} • Ref: {{depositReference}}{{/depositReference}}
              </td>
              <td>-{{depositAppliedAmount}}</td>
            </tr>
            {{/depositAppliedToInvoice}}
            {{^depositAppliedToInvoice}}
            <tr>
              <td colspan="2">
                Deposit received{{#depositPercentage}} ({{depositPercentage}}%){{/depositPercentage}} — not applied to this invoice
              </td>
              <td>{{depositAmount}}</td>
            </tr>
            {{/depositAppliedToInvoice}}
            {{#depositRemaining}}
            <tr>
              <td colspan="2">Deposit remaining</td>
              <td>{{depositRemaining}}</td>
            </tr>
            {{/depositRemaining}}
          {{/hasDeposit}}
          {{/isDepositInvoice}}

          {{#otherPaymentsAppliedToInvoice}}
          <tr>
            <td colspan="2">Less: Other payments to date</td>
            <td>-{{otherPaymentsTotal}}</td>
          </tr>
          {{/otherPaymentsAppliedToInvoice}}

          <tr class="total-row">
            <td colspan="2"><strong>Balance Due</strong></td>
            <td><strong>{{balanceDue}}</strong></td>
          </tr>
        </tfoot>
      </table>
      {{/hasLineItems}}

      {{#depositInvoiceReference}}
      <p class="meta-note">{{depositAlreadyInvoicedNarrative}}</p>
      {{/depositInvoiceReference}}

      <!-- VAT inclusivity note -->
      {{#vatEnabled}}
      {{#vatInclusiveDocument}}
      <p class="meta-note">Prices are VAT-inclusive.</p>
      {{/vatInclusiveDocument}}
      {{^vatInclusiveDocument}}
      <p class="meta-note">Prices are VAT-exclusive; VAT added in summary.</p>
      {{/vatInclusiveDocument}}
      {{/vatEnabled}}

      <!-- Payment details -->
      {{#paymentInstructions}}
      <div class="payment-section">
        <h3>Payment</h3>
        <p>{{paymentInstructions}}</p>
      </div>
      {{/paymentInstructions}}
      {{#bankAccountName}}
      <div class="bank-section">
        <h4>Bank Details</h4>
        <p>
          {{#bankName}}Bank: {{bankName}}<br />{{/bankName}}
          Account Name: {{bankAccountName}}<br />
          {{#bankAccountNumber}}Account No: {{bankAccountNumber}}<br />{{/bankAccountNumber}}
          {{#bankSortCode}}Sort Code: {{bankSortCode}}{{/bankSortCode}}
        </p>
      </div>
      {{/bankAccountName}}

      {{#isDepositInvoice}}
      <p>Your order will be placed and your installation booking secured once cleared funds are received.</p>
      {{/isDepositInvoice}}

      <!-- Terms -->
      {{#businessTerms}}
      <div class="terms-section">
        <h3>Terms & Conditions</h3>
        <div class="terms-content">{{{businessTerms}}}</div>
      </div>
      {{/businessTerms}}

      <!-- Footer -->
      <div class="footer">
        {{#companyWebsite}}<span>{{companyWebsite}}</span>{{/companyWebsite}}
        {{#companyEmail}}<span> • {{companyEmail}}</span>{{/companyEmail}}
        {{#companyPhone}}<span> • {{companyPhone}}</span>{{/companyPhone}}
      </div>

    </div>`
    ,
    CISCertificate: `<div class="document-template">
      <div class="header">
        <div class="header-content">
          <div class="title">CONSTRUCTION INDUSTRY SCHEME (CIS)</div>
          <div class="subtitle">Payment and Deduction Statement</div>
          <div class="date">Tax Month (6th–5th): {{taxMonthLabel}}</div>
        </div>
        <div class="logo-section">
          {{#logoPath}}
          <img src="{{logoPath}}" alt="Company Logo" class="logo" />
          {{/logoPath}}
        </div>
      </div>

      <div class="info-grid">
        <div class="info-section">
          <h3>Contractor Details</h3>
          <p>
            <strong>Contractor Name:</strong> {{contractorName}}<br />
            <strong>Trading Name (if different):</strong> {{contractorTradingName}}<br />
            <strong>Contractor UTR:</strong> {{contractorUtr}}<br />
            <strong>Company Registration Number (if Ltd):</strong> {{contractorCompanyReg}}<br />
            <strong>Address:</strong> {{contractorAddress}}<br />
            <strong>Postcode:</strong> {{contractorPostcode}}
          </p>
        </div>
        <div class="info-section">
          <h3>Subcontractor Details</h3>
          <p>
            <strong>Subcontractor Name:</strong> {{subcontractorName}}<br />
            <strong>Trading Name (if different):</strong> {{subcontractorTradingName}}<br />
            <strong>Subcontractor UTR:</strong> {{subcontractorUtr}}<br />
            <strong>National Insurance No. (if sole trader):</strong> {{subcontractorNi}}<br />
            <strong>Company Registration Number (if Ltd):</strong> {{subcontractorCompanyReg}}<br />
            <strong>Address:</strong> {{subcontractorAddress}}
          </p>
        </div>
      </div>

      <div class="description-section">
        <h3>Payment Details</h3>
        <p>
          <strong>Verification Number (if applicable):</strong> {{verificationNumber}}<br />
          <strong>Payment Date:</strong> {{paymentDate}}
        </p>
      </div>

      <table class="items-table">
        <thead>
          <tr>
            <th>Description of Work</th>
            <th style="text-align:right;">Gross Amount (£)</th>
          </tr>
        </thead>
        <tbody>
          {{workRowsHtml}}
        </tbody>
      </table>

      <div class="description-section">
        <h3>Totals</h3>
        <p>
          <strong>Total Labour (Subject to CIS):</strong> {{totalLabour}}<br />
          <strong>Total Materials (Not Subject to CIS):</strong> {{totalMaterials}}<br />
          <strong>Other Adjustments (if any):</strong> {{otherAdjustments}}
        </p>
      </div>

      <div class="description-section">
        <h3>CIS Calculation</h3>
        <p>
          <strong>Gross Amount Subject to CIS:</strong> {{grossSubjectToCis}}<br />
          <strong>CIS Deduction Rate:</strong> {{cisRate0}} 0% &nbsp; {{cisRate20}} 20% &nbsp; {{cisRate30}} 30%<br />
          <strong>CIS Tax Deducted:</strong> {{cisTaxDeducted}}
        </p>
      </div>

      <div class="description-section">
        <h3>Summary</h3>
        <p>
          <strong>Total Gross Payment:</strong> {{totalGrossPayment}}<br />
          <strong>Less CIS Deduction:</strong> {{lessCisDeduction}}<br />
          <strong>Net Amount Paid:</strong> {{netAmountPaid}}
        </p>
      </div>

      <div class="description-section">
        <h3>Declaration</h3>
        <p>This statement is provided in accordance with the Construction Industry Scheme (CIS).</p>
        <p>The contractor confirms that the above deduction has been made and will be paid to HMRC.</p>
        <p>
          <strong>Signed:</strong> _______________________________<br />
          <strong>Name:</strong> ________________________________<br />
          <strong>Position:</strong> _____________________________<br />
          <strong>Date:</strong> {{generationDate}}
        </p>
      </div>
    </div>`,
    AccountInvoice: KSU_INVOICE_TEMPLATE,
  });

  // Load organisations and configs
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [orgs, configs] = await Promise.all([
          documentsService.listOrganisations(),
          pdfExportConfigService.getAllConfigs(),
        ]);
        setOrganisations(orgs);
        setConfigs(configs);
        
        // Admin → Organisations links here with ?org=<code>; otherwise pick the first
        const wanted = new URLSearchParams(window.location.search).get('org');
        const linked = wanted ? orgs.find((org) => org.code === wanted || org.id === wanted) : undefined;
        if (orgs.length > 0 && !selectedOrgId) {
          setSelectedOrgId((linked || orgs[0]).id);
        }
      } catch (err) {
        setError('Failed to load data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Load config when organisation or document type changes
  useEffect(() => {
    const loadConfig = async () => {
      if (!selectedOrgId) return;
      
      try {
        setLoading(true);
        const [config, template] = await Promise.all([
          pdfExportConfigService.getConfigByOrganisation(selectedOrgId, selectedDocType),
          documentTemplateService.getTemplate(selectedOrgId, selectedDocType)
        ]);
        
        if (config) {
          setCurrentConfig(config);
          setFormData({
            organisationId: config.organisationId,
            documentType: config.documentType,
            companyName: config.companyName,
            companyTradingName: (config as any).companyTradingName || '',
            companyUtr: (config as any).companyUtr || '',
            companyAddress: config.companyAddress || '',
            companyPhone: config.companyPhone || '',
            companyEmail: config.companyEmail || '',
            companyVatNumber: config.companyVatNumber || '',
            companyRegistrationNumber: config.companyRegistrationNumber || '',
            bankName: (config as any).bankName || '',
            bankAccountName: (config as any).bankAccountName || '',
            bankAccountNumber: (config as any).bankAccountNumber || '',
            bankSortCode: (config as any).bankSortCode || '',
            bankIban: (config as any).bankIban || '',
            bankSwift: (config as any).bankSwift || '',
            logoPath: config.logoPath || '',
            logoWidth: config.logoWidth,
            logoHeight: config.logoHeight,
            headerImagePath: config.headerImagePath || '',
            headerTitle: config.headerTitle,
            headerSubtitle: config.headerSubtitle || '',
            headerColor: config.headerColor,
            footerImagePath: config.footerImagePath || '',
            footerText: config.footerText,
            showGenerationDate: config.showGenerationDate,
            showPageNumbers: config.showPageNumbers,
            primaryColor: config.primaryColor,
            secondaryColor: config.secondaryColor,
            fontFamily: config.fontFamily,
            fontSizeBase: config.fontSizeBase,
            businessRegisteredAddress: config.businessRegisteredAddress || '',
            businessWebsite: config.businessWebsite || '',
            businessTerms: config.businessTerms || '',
          });
        } else {
          setCurrentConfig(null);
          setFormData(prev => ({ 
            ...prev, 
            organisationId: selectedOrgId,
            documentType: selectedDocType 
          }));
        }

        // Load template content
        if (template) {
          setTemplateContent(prev => ({
            ...prev,
            [selectedDocType]: template.template
          }));
        } else {
          // Load default template if none exists
          const defaultTemplate = await documentTemplateService.getDefaultTemplate(selectedDocType);
          setTemplateContent(prev => ({
            ...prev,
            [selectedDocType]: defaultTemplate
          }));
        }
      } catch (err) {
        setError('Failed to load configuration');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    loadConfig();
  }, [selectedOrgId, selectedDocType]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      
      console.log('Saving configuration with data:', {
        ...formData,
        organisationId: selectedOrgId,
        documentType: selectedDocType
      });
      
      // Save both the configuration and the template
      const [updatedConfig, savedTemplate] = await Promise.all([
        pdfExportConfigService.upsertConfig({
          ...formData,
          organisationId: selectedOrgId,
          documentType: selectedDocType
        }),
        documentTemplateService.saveTemplate({
          organisationId: selectedOrgId,
          documentType: selectedDocType,
          template: templateContent[selectedDocType || 'Quotation']
        })
      ]);
      
      console.log('Configuration saved successfully:', updatedConfig);
      console.log('Template saved successfully:', savedTemplate);
      setCurrentConfig(updatedConfig);
      
      // Update the configs list
      setConfigs(prev => {
        const existingIndex = prev.findIndex(c => c.id === updatedConfig.id);
        if (existingIndex >= 0) {
          const newConfigs = [...prev];
          newConfigs[existingIndex] = updatedConfig;
          return newConfigs;
        } else {
          return [updatedConfig, ...prev];
        }
      });
      
      setSuccess('Configuration and template saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving configuration:', err);
      setError('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (currentConfig) {
      setFormData({
        organisationId: currentConfig.organisationId,
        documentType: currentConfig.documentType,
        companyName: currentConfig.companyName,
        companyTradingName: (currentConfig as any).companyTradingName || '',
        companyUtr: (currentConfig as any).companyUtr || '',
        companyAddress: currentConfig.companyAddress || '',
        companyPhone: currentConfig.companyPhone || '',
        companyEmail: currentConfig.companyEmail || '',
        companyVatNumber: currentConfig.companyVatNumber || '',
        companyRegistrationNumber: currentConfig.companyRegistrationNumber || '',
        bankName: (currentConfig as any).bankName || '',
        bankAccountName: (currentConfig as any).bankAccountName || '',
        bankAccountNumber: (currentConfig as any).bankAccountNumber || '',
        bankSortCode: (currentConfig as any).bankSortCode || '',
        bankIban: (currentConfig as any).bankIban || '',
        bankSwift: (currentConfig as any).bankSwift || '',
        logoPath: currentConfig.logoPath || '',
        logoWidth: currentConfig.logoWidth,
        logoHeight: currentConfig.logoHeight,
        headerImagePath: currentConfig.headerImagePath || '',
        headerTitle: currentConfig.headerTitle,
        headerSubtitle: currentConfig.headerSubtitle || '',
        headerColor: currentConfig.headerColor,
        footerImagePath: currentConfig.footerImagePath || '',
        footerText: currentConfig.footerText,
        showGenerationDate: currentConfig.showGenerationDate,
        showPageNumbers: currentConfig.showPageNumbers,
        primaryColor: currentConfig.primaryColor,
        secondaryColor: currentConfig.secondaryColor,
        fontFamily: currentConfig.fontFamily,
        fontSizeBase: currentConfig.fontSizeBase,
        businessRegisteredAddress: currentConfig.businessRegisteredAddress || '',
        businessWebsite: currentConfig.businessWebsite || '',
        businessTerms: currentConfig.businessTerms || '',
      });
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      // Match header/footer behavior: store a public path reference only.
      // Place the file into /public/pdf-export-assets/ in the app for it to load.
      const filePath = `/pdf-export-assets/${file.name}`;
      setFormData(prev => ({ ...prev, logoPath: filePath }));
      setSuccess(`Logo file "${file.name}" selected. Path: ${filePath}`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error handling logo upload:', error);
      setError('Failed to process logo upload');
    } finally {
      try { event.target.value = ''; } catch {}
    }
  };

  const handleHeaderImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      const filePath = `/pdf-export-assets/${file.name}`;
      setFormData(prev => ({ ...prev, headerImagePath: filePath }));
      setSuccess(`Header image "${file.name}" selected. Path: ${filePath}`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error handling header image upload:', error);
      setError('Failed to process header image upload');
    } finally {
      try { event.target.value = ''; } catch {}
    }
  };

  const handleFooterImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      const filePath = `/pdf-export-assets/${file.name}`;
      setFormData(prev => ({ ...prev, footerImagePath: filePath }));
      setSuccess(`Footer image "${file.name}" selected. Path: ${filePath}`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error handling footer image upload:', error);
      setError('Failed to process footer image upload');
    } finally {
      try { event.target.value = ''; } catch {}
    }
  };

  const generatePreviewHtml = () => {
    const { 
      companyName, companyAddress, companyPhone, companyEmail, companyVatNumber,
      bankName, bankAccountName, bankAccountNumber, bankSortCode, bankIban, bankSwift,
      logoPath, headerImagePath, footerImagePath, headerTitle, headerSubtitle, headerColor, primaryColor, secondaryColor,
      fontFamily, fontSizeBase
    } = formData;

    // Get the current template content for the selected document type
    const currentTemplate = templateContent[selectedDocType || 'Quotation'] || '';
    
    // Replace Handlebars variables with sample data for preview
    const previewContent = normalizeDescriptionTemplateMarkup(currentTemplate)
      .replace(/\{\{headerTitle\}\}/g, headerTitle || '')
      .replace(/\{\{documentType\}\}/g, selectedDocType)
      .replace(/\{\{documentArticle\}\}/g, /^[AEIOUaeiou]/.test(selectedDocType) ? 'an' : 'a')
      .replace(/\{\{documentRef\}\}/g, 'SAMPLE-REF-001')
      .replace(/\{\{documentTypeLower\}\}/g, (selectedDocType || '').toLowerCase())
      .replace(/\{\{documentDate\}\}/g, new Date().toLocaleDateString('en-GB'))
      .replace(/\{\{companyName\}\}/g, companyName || '')
      .replace(/\{\{documentTitle\}\}/g, 'Sample Project Title')
      .replace(/\{\{companyAddress\}\}/g, companyAddress || '')
      .replace(/\{\{companyPhone\}\}/g, companyPhone || '')
      .replace(/\{\{companyEmail\}\}/g, companyEmail || '')
      .replace(/\{\{companyVatNumber\}\}/g, companyVatNumber || '')
      .replace(/\{\{bankName\}\}/g, bankName || '')
      .replace(/\{\{bankAccountName\}\}/g, bankAccountName || '')
      .replace(/\{\{bankAccountNumber\}\}/g, bankAccountNumber || '')
      .replace(/\{\{bankSortCode\}\}/g, bankSortCode || '')
      .replace(/\{\{bankIban\}\}/g, bankIban || '')
      .replace(/\{\{bankSwift\}\}/g, bankSwift || '')
      .replace(/\{\{customerName\}\}/g, 'John Smith')
      .replace(/\{\{customerTitle\}\}/g, 'Mr')
      .replace(/\{\{customerAddress\}\}/g, '123 Sample Street, Sample City, AB12 3CD')
      .replace(/\{\{description\}\}/g, '<p>Sample project description for preview purposes.</p><p>This paragraph demonstrates rich text formatting.</p>')
      .replace(/\{\{hasLineItems\}\}/g, 'true')
      .replace(/\{\{lineItems\}\}/g, `
        <tr>
          <td><strong>Sample Item</strong><br /><span class="item-description">Sample description</span></td>
          <td>2</td>
          <td>pieces</td>
          <td>£25.00</td>
          <td>£50.00</td>
        </tr>
      `)
      .replace(/\{\{baseCostTotal\}\}/g, '£50.00')
      .replace(/\{\{showFees\}\}/g, 'true')
      .replace(/\{\{adminPct\}\}/g, '5')
      .replace(/\{\{adminFee\}\}/g, '£2.50')
      .replace(/\{\{contingencyPct\}\}/g, '10')
      .replace(/\{\{contingency\}\}/g, '£5.00')
      .replace(/\{\{profitPct\}\}/g, '20')
      .replace(/\{\{profit\}\}/g, '£10.00')
      .replace(/\{\{vatEnabled\}\}/g, 'true')
      .replace(/\{\{vatPct\}\}/g, '20')
      .replace(/\{\{vatAmount\}\}/g, '£13.50')
      .replace(/\{\{grandTotal\}\}/g, '£81.00')
      .replace(/\{\{totalExVat\}\}/g, '£67.50')
      .replace(/\{\{totalIncVat\}\}/g, '£81.00')
      .replace(/\{\{materialsInclVat\}\}/g, '£36.00')
      .replace(/\{\{materialsExcVat\}\}/g, '£30.00')
      .replace(/\{\{labourInclVat\}\}/g, '£45.00')
      .replace(/\{\{labourExcVat\}\}/g, '£37.50')
      .replace(/\{\{labourDays\}\}/g, '3')
      .replace(/\{\{labourHours\}\}/g, '6')
      .replace(/\{\{labourDaysText\}\}/g, '3 days')
      .replace(/\{\{labourHoursText\}\}/g, '6 hours')
      .replace(/\{\{logoPath\}\}/g, logoPath || '')
      .replace(/\{\{#logoPath\}([\s\S]*?)\{\{\/logoPath\}\}/g, logoPath ? '$1' : '')
      .replace(/\{\{#companyAddress\}([\s\S]*?)\{\{\/companyAddress\}\}/g, companyAddress ? '$1' : '')
      .replace(/\{\{#companyPhone\}([\s\S]*?)\{\{\/companyPhone\}\}/g, companyPhone ? '$1' : '')
      .replace(/\{\{#companyEmail\}([\s\S]*?)\{\{\/companyEmail\}\}/g, companyEmail ? '$1' : '')
      .replace(/\{\{#companyVatNumber\}([\s\S]*?)\{\{\/companyVatNumber\}\}/g, companyVatNumber ? '$1' : '')
      .replace(/\{\{#bankName\}([\s\S]*?)\{\{\/bankName\}\}/g, bankName ? '$1' : '')
      .replace(/\{\{#bankAccountName\}([\s\S]*?)\{\{\/bankAccountName\}\}/g, bankAccountName ? '$1' : '')
      .replace(/\{\{#bankAccountNumber\}([\s\S]*?)\{\{\/bankAccountNumber\}\}/g, bankAccountNumber ? '$1' : '')
      .replace(/\{\{#bankSortCode\}([\s\S]*?)\{\{\/bankSortCode\}\}/g, bankSortCode ? '$1' : '')
      .replace(/\{\{#bankIban\}([\s\S]*?)\{\{\/bankIban\}\}/g, bankIban ? '$1' : '')
      .replace(/\{\{#bankSwift\}([\s\S]*?)\{\{\/bankSwift\}\}/g, bankSwift ? '$1' : '')
      .replace(/\{\{#customerTitle\}([\s\S]*?)\{\{\/customerTitle\}\}/g, 'true')
      .replace(/\{\{#customerAddress\}([\s\S]*?)\{\{\/customerAddress\}\}/g, 'true')
      .replace(/\{\{#description\}([\s\S]*?)\{\{\/description\}\}/g, 'true')
      .replace(/\{\{#showFees\}([\s\S]*?)\{\{\/showFees\}\}/g, 'true')
      .replace(/\{\{#vatEnabled\}([\s\S]*?)\{\{\/vatEnabled\}\}/g, 'true')
      .replace(/\{\{\^vatEnabled\}([\s\S]*?)\{\{\/vatEnabled\}\}/g, '')
      .replace(/\{\{#labourDays\}([\s\S]*?)\{\{\/labourDays\}\}/g, '$1') // Show labour days content in preview
      .replace(/\{\{#labourHours\}([\s\S]*?)\{\{\/labourHours\}\}/g, '$1') // Show labour hours content in preview
      .replace(/\{\{#labourDaysText\}([\s\S]*?)\{\{\/labourDaysText\}\}/g, '$1') // Show formatted days text in preview
      .replace(/\{\{#labourHoursText\}([\s\S]*?)\{\{\/labourHoursText\}\}/g, '$1'); // Show formatted hours text in preview

    // CIS Certificate preview values (only apply when template uses these vars)
    const withCisPreview = previewContent
      .replace(/\{\{taxMonthLabel\}\}/g, 'February - March 2026')
      .replace(/\{\{contractorName\}\}/g, companyName || 'Keystone Glazing')
      .replace(/\{\{contractorTradingName\}\}/g, formData.companyTradingName || '')
      .replace(/\{\{contractorUtr\}\}/g, formData.companyUtr || '')
      .replace(/\{\{contractorCompanyReg\}\}/g, formData.companyRegistrationNumber || '')
      .replace(/\{\{contractorAddress\}\}/g, companyAddress || '')
      .replace(/\{\{contractorPostcode\}\}/g, '')
      .replace(/\{\{subcontractorName\}\}/g, 'Sample Subcontractor')
      .replace(/\{\{subcontractorTradingName\}\}/g, '')
      .replace(/\{\{subcontractorUtr\}\}/g, '')
      .replace(/\{\{subcontractorNi\}\}/g, '')
      .replace(/\{\{subcontractorCompanyReg\}\}/g, '')
      .replace(/\{\{subcontractorAddress\}\}/g, '123 Sample Street, Sample City')
      .replace(/\{\{verificationNumber\}\}/g, '')
      .replace(/\{\{paymentDate\}\}/g, '06/02/2026 – 05/03/2026')
      .replace(/\{\{workRowsHtml\}\}/g, `
        <tr><td>Invoice #12345 • General glazing work</td><td style="text-align:right;">£960.00</td></tr>
      `)
      .replace(/\{\{totalLabour\}\}/g, '£960.00')
      .replace(/\{\{totalMaterials\}\}/g, '£0.00')
      .replace(/\{\{otherAdjustments\}\}/g, '£0.00')
      .replace(/\{\{grossSubjectToCis\}\}/g, '£960.00')
      .replace(/\{\{cisRate0\}\}/g, '[ ]')
      .replace(/\{\{cisRate20\}\}/g, '[X]')
      .replace(/\{\{cisRate30\}\}/g, '[ ]')
      .replace(/\{\{cisTaxDeducted\}\}/g, '£192.00')
      .replace(/\{\{totalGrossPayment\}\}/g, '£960.00')
      .replace(/\{\{lessCisDeduction\}\}/g, '£192.00')
      .replace(/\{\{netAmountPaid\}\}/g, '£768.00')
      .replace(/\{\{generationDate\}\}/g, new Date().toLocaleDateString('en-GB'));

    let cleaned = withCisPreview
      .replace(/<p>\s*[{}]+\s*<\/p>/g, '')
      .replace(/<div>\s*[{}]+\s*<\/div>/g, '');

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
          body { 
            font-family: ${fontFamily?.includes('Poppins') ? fontFamily : `Poppins, ${fontFamily}`}; 
            font-size: ${fontSizeBase}px; 
            margin: 0; 
            padding: 20px; 
            color: #333;
          }
          .header { 
            display: flex; 
            justify-content: space-between; 
            align-items: center; 
            margin-bottom: 30px; 
            border-bottom: 2px solid ${headerColor}; 
            padding-bottom: 15px;
          }
          .title { 
            font-size: 24px; 
            font-weight: bold; 
            color: ${primaryColor};
          }
          .subtitle { 
            font-size: 16px; 
            color: ${secondaryColor}; 
            margin-top: 5px;
          }
          .company-info { 
            font-size: 14px; 
            color: ${secondaryColor};
          }
          .logo { 
            width: ${formData.logoWidth}px; 
            height: ${formData.logoHeight}px; 
            object-fit: contain;
            border: 1px solid #ddd;
          }
          .header-image img, .footer-image img {
            max-width: 100%;
            height: auto;
          }
          .document-template { margin: 0; }
          .info-grid { display: flex; gap: 24px; margin: 24px 0; }
          .info-section { flex: 1; padding: 0; margin: 0; display: flex; flex-direction: column; }
          .info-section h3 { margin: 0 0 8px 0; padding: 0; font-size: 14px; text-transform: uppercase; color: ${secondaryColor}; line-height: 1.2; }
          .info-section p { margin: 0; padding: 0; line-height: 1.4; flex: 1; }
          .items-table { width: 100%; border-collapse: collapse; margin-top: 16px; font-size: 14px; }
          .items-table th, .items-table td { border: 1px solid #ddd; padding: 12px 8px; text-align: left; }
          .items-table th { background-color: #f8f9fa; font-weight: bold; }
          .items-table tfoot td { font-weight: normal; background-color: #f8f9fa; }
          .total-row { font-weight: inherit; font-size: 18px; border-top: 1px solid #ddd; padding-top: 8px; margin-top: 8px; }
          .total-row strong, .total-row b { font-weight: normal; }
        </style>
      </head>
      <body>
        ${headerImagePath ? `<div class="header-image"><img src="${headerImagePath}" alt="Header Image" /></div>` : ''}
        ${cleaned}
        ${footerImagePath ? `<div class="footer-image"><img src="${footerImagePath}" alt="Footer Image" /></div>` : ''}
      </body>
      </html>
    `;
  };

  const openPreview = () => {
    const html = generatePreviewHtml();
    const w = window.open('', '_blank');
    if (w) {
      w.document.write(html);
      w.document.close();
    }
  };

  if (loading) {
    return <Typography>Loading configuration...</Typography>;
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      <Typography level="h3" sx={{ mb: 3 }}>PDF Export Configuration</Typography>
      
      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>
      )}
      
      {success && (
        <Alert color="success" sx={{ mb: 2 }}>{success}</Alert>
      )}

      {/* Organisation Selector */}
      <Card sx={{ p: 2, mb: 3 }}>
        <Typography level="title-md" sx={{ mb: 2 }}>Select Organisation</Typography>
        <Select
          value={selectedOrgId}
          onChange={(_, value) => setSelectedOrgId(value || '')}
          placeholder="Select organisation"
        >
          {organisations.map(org => (
            <Option key={org.id} value={org.id}>
              {org.name} ({org.code})
            </Option>
          ))}
        </Select>
      </Card>

      {/* Document Type Selector */}
      {selectedOrgId && (
        <Card sx={{ p: 2, mb: 3 }}>
          <Typography level="title-md" sx={{ mb: 2 }}>Select Document Type</Typography>
          <Select
            value={selectedDocType}
            onChange={(_, value) => setSelectedDocType(value as TemplateKind)}
            placeholder="Select document type"
          >
            <Option value="Quotation">Quotation</Option>
            <Option value="Estimate">Estimate</Option>
            <Option value="Invoice">Invoice</Option>
            <Option value="CISCertificate">CIS Certificates</Option>
            <Option value="AccountInvoice">Account invoices (Invoicing page)</Option>
          </Select>
        </Card>
      )}

      {selectedOrgId && (
        <>
          {/* Main Configuration Tabs */}
          <Card sx={{ p: 0, overflow: 'hidden' }}>
            <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value as number)}>
              <TabList>
                <Tab>Configuration</Tab>
                <Tab>Document Templates</Tab>
                <Tab>Preview</Tab>
              </TabList>

              {/* Configuration Tab */}
              <TabPanel value={0}>
                <Box sx={{ p: 3 }}>
                  {/* Company Information */}
                  <Card sx={{ p: 2, mb: 3 }}>
                    <Typography level="title-md" sx={{ mb: 2 }}>Company Information</Typography>
                    <Grid container spacing={2}>
                      <Grid xs={12} md={6}>
                        <FormControl>
                          <FormLabel>Company Name *</FormLabel>
                          <Input
                            value={formData.companyName}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, companyName: e.target.value }))}
                            placeholder="Enter company name"
                          />
                        </FormControl>
                      </Grid>
                      <Grid xs={12} md={6}>
                        <FormControl>
                          <FormLabel>Trading Name (if different)</FormLabel>
                          <Input
                            value={formData.companyTradingName || ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, companyTradingName: e.target.value }))}
                            placeholder="Enter trading name"
                          />
                        </FormControl>
                      </Grid>
                      <Grid xs={12} md={6}>
                        <FormControl>
                          <FormLabel>Contractor UTR</FormLabel>
                          <Input
                            value={formData.companyUtr || ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, companyUtr: e.target.value }))}
                            placeholder="Enter UTR"
                          />
                        </FormControl>
                      </Grid>
                      <Grid xs={12} md={6}>
                        <FormControl>
                          <FormLabel>Company Registration Number (if Ltd)</FormLabel>
                          <Input
                            value={formData.companyRegistrationNumber || ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, companyRegistrationNumber: e.target.value }))}
                            placeholder="Enter company number"
                          />
                        </FormControl>
                      </Grid>
                      <Grid xs={12} md={6}>
                        <FormControl>
                          <FormLabel>VAT Number</FormLabel>
                          <Input
                            value={formData.companyVatNumber || ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, companyVatNumber: e.target.value }))}
                            placeholder="GB123456789"
                          />
                        </FormControl>
                      </Grid>
                      <Grid xs={12}>
                        <FormControl>
                          <FormLabel>Company Address</FormLabel>
                          <Textarea
                            value={formData.companyAddress || ''}
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFormData(prev => ({ ...prev, companyAddress: e.target.value }))}
                            placeholder="Enter full company address"
                            minRows={2}
                          />
                        </FormControl>
                      </Grid>
                      <Grid xs={12} md={6}>
                        <FormControl>
                          <FormLabel>Phone Number</FormLabel>
                          <Input
                            value={formData.companyPhone || ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, companyPhone: e.target.value }))}
                            placeholder="Enter phone number"
                          />
                        </FormControl>
                      </Grid>
                      <Grid xs={12} md={6}>
                        <FormControl>
                          <FormLabel>Email Address</FormLabel>
                          <Input
                            value={formData.companyEmail || ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, companyEmail: e.target.value }))}
                            placeholder="Enter email address"
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Card>

                  {/* Banking Details (Invoices) */}
                  {selectedDocType === 'Invoice' && (
                    <Card sx={{ p: 2, mb: 3 }}>
                      <Typography level="title-md" sx={{ mb: 2 }}>Banking Details</Typography>
                      <Typography level="body-sm" sx={{ mb: 2, color: 'neutral.500' }}>
                        These appear in your invoice templates via {'{{bankName}}'}, {'{{bankAccountName}}'}, {'{{bankAccountNumber}}'}, {'{{bankSortCode}}'}, {'{{bankIban}}'}, {'{{bankSwift}}'}.
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid xs={12} md={6}>
                          <FormControl>
                            <FormLabel>Bank Name</FormLabel>
                            <Input
                              value={formData.bankName || ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, bankName: e.target.value }))}
                              placeholder="e.g. Barclays"
                            />
                          </FormControl>
                        </Grid>
                        <Grid xs={12} md={6}>
                          <FormControl>
                            <FormLabel>Account Name</FormLabel>
                            <Input
                              value={formData.bankAccountName || ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, bankAccountName: e.target.value }))}
                              placeholder="e.g. Keystone Glazing Ltd"
                            />
                          </FormControl>
                        </Grid>
                        <Grid xs={12} md={6}>
                          <FormControl>
                            <FormLabel>Account Number</FormLabel>
                            <Input
                              value={formData.bankAccountNumber || ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, bankAccountNumber: e.target.value }))}
                              placeholder="e.g. 12345678"
                            />
                          </FormControl>
                        </Grid>
                        <Grid xs={12} md={6}>
                          <FormControl>
                            <FormLabel>Sort Code</FormLabel>
                            <Input
                              value={formData.bankSortCode || ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, bankSortCode: e.target.value }))}
                              placeholder="e.g. 12-34-56"
                            />
                          </FormControl>
                        </Grid>
                        <Grid xs={12} md={6}>
                          <FormControl>
                            <FormLabel>IBAN</FormLabel>
                            <Input
                              value={formData.bankIban || ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, bankIban: e.target.value }))}
                              placeholder="e.g. GB00BARC12345612345678"
                            />
                          </FormControl>
                        </Grid>
                        <Grid xs={12} md={6}>
                          <FormControl>
                            <FormLabel>SWIFT / BIC</FormLabel>
                            <Input
                              value={formData.bankSwift || ''}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, bankSwift: e.target.value }))}
                              placeholder="e.g. BARCGB22"
                            />
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Card>
                  )}

                  {/* Logo Settings */}
                  <Card sx={{ p: 2, mb: 3 }}>
                    <Typography level="title-md" sx={{ mb: 2 }}>Logo Settings</Typography>
                    <Typography level="body-sm" sx={{ mb: 2, color: 'neutral.500' }}>
                      Upload a company logo to display in the PDF header. Leave blank to exclude the logo.
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid xs={12} md={6}>
                        <FormControl>
                          <FormLabel>Logo Upload</FormLabel>
                          <Input
                            slotProps={{
                              input: {
                                type: 'file',
                                accept: 'image/*',
                                onChange: handleLogoUpload
                              }
                            }}
                            startDecorator={<IconWrapper icon={FaUpload} />}
                          />
                        </FormControl>
                        {formData.logoPath && (
                          <Box sx={{ mt: 1 }}>
                            <Typography level="body-sm" sx={{ color: 'success.500', mb: 1 }}>
                              Logo Path: {formData.logoPath}
                            </Typography>
                            <Button
                              size="sm"
                              variant="outlined"
                              color="danger"
                              onClick={() => setFormData(prev => ({ ...prev, logoPath: '' }))}
                            >
                              Remove Logo
                            </Button>
                          </Box>
                        )}
                      </Grid>
                      <Grid xs={12} md={3}>
                        <FormControl>
                          <FormLabel>Logo Width (px)</FormLabel>
                          <Input
                            type="number"
                            value={formData.logoWidth}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, logoWidth: parseInt(e.target.value) || 200 }))}
                          />
                        </FormControl>
                      </Grid>
                      <Grid xs={12} md={3}>
                        <FormControl>
                          <FormLabel>Logo Height (px)</FormLabel>
                          <Input
                            type="number"
                            value={formData.logoHeight}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, logoHeight: parseInt(e.target.value) || 100 }))}
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Card>

                  {/* Header Settings */}
                  <Card sx={{ p: 2, mb: 3 }}>
                    <Typography level="title-md" sx={{ mb: 2 }}>Document Header</Typography>
                    <Grid container spacing={2}>
                      <Grid xs={12} md={6}>
                        <FormControl>
                          <FormLabel>Header Title</FormLabel>
                          <Input
                            value={formData.headerTitle}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, headerTitle: e.target.value }))}
                            placeholder="Enter header title"
                          />
                        </FormControl>
                      </Grid>
                      <Grid xs={12} md={6}>
                        <FormControl>
                          <FormLabel>Header Image Upload (File Path)</FormLabel>
                          <Input
                            slotProps={{
                              input: {
                                type: 'file',
                                accept: 'image/*',
                                onChange: handleHeaderImageUpload
                              }
                            }}
                            startDecorator={<IconWrapper icon={FaUpload} />}
                          />
                        </FormControl>
                        {formData.headerImagePath && (
                          <Box sx={{ mt: 1 }}>
                            <Typography level="body-sm" sx={{ color: 'success.500', mb: 1 }}>
                              Header Image Path: {formData.headerImagePath}
                            </Typography>
                            <Button
                              size="sm"
                              variant="outlined"
                              color="danger"
                              onClick={() => setFormData(prev => ({ ...prev, headerImagePath: '' }))}
                            >
                              Remove Header Image
                            </Button>
                          </Box>
                        )}
                      </Grid>
                      <Grid xs={12} md={6}>
                        <FormControl>
                          <FormLabel>Header Subtitle</FormLabel>
                          <Input
                            value={formData.headerSubtitle || ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, headerSubtitle: e.target.value }))}
                            placeholder="Enter header subtitle"
                          />
                        </FormControl>
                      </Grid>
                      <Grid xs={12} md={6}>
                        <FormControl>
                          <FormLabel>Header Color</FormLabel>
                          <Input
                            type="color"
                            value={formData.headerColor}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, headerColor: e.target.value }))}
                          />
                        </FormControl>
                      </Grid>
                      <Grid xs={12} md={6}>
                        <FormControl>
                          <FormLabel>Primary Color</FormLabel>
                          <Input
                            type="color"
                            value={formData.primaryColor}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData(prev => ({ ...prev, primaryColor: e.target.value }))}
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Card>

                  {/* Footer Settings */}
                  <Card sx={{ p: 2, mb: 3 }}>
                    <Typography level="title-md" sx={{ mb: 2 }}>Footer Settings</Typography>
                    <Grid container spacing={2}>
                      <Grid xs={12} md={6}>
                        <FormControl>
                          <FormLabel>Footer Image Upload (File Path)</FormLabel>
                          <Input
                            slotProps={{
                              input: {
                                type: 'file',
                                accept: 'image/*',
                                onChange: handleFooterImageUpload
                              }
                            }}
                            startDecorator={<IconWrapper icon={FaUpload} />}
                          />
                        </FormControl>
                        {formData.footerImagePath && (
                          <Box sx={{ mt: 1 }}>
                            <Typography level="body-sm" sx={{ color: 'success.500', mb: 1 }}>
                              Footer Image Path: {formData.footerImagePath}
                            </Typography>
                            <Button
                              size="sm"
                              variant="outlined"
                              color="danger"
                              onClick={() => setFormData(prev => ({ ...prev, footerImagePath: '' }))}
                            >
                              Remove Footer Image
                            </Button>
                          </Box>
                        )}
                      </Grid>
                      <Grid xs={12} md={6}>
                        <FormControl>
                          <FormLabel>Footer Text</FormLabel>
                          <Textarea
                            value={formData.footerText}
                            onChange={(e) => setFormData(prev => ({ ...prev, footerText: e.target.value }))}
                            placeholder="Enter footer text"
                            minRows={2}
                          />
                        </FormControl>
                      </Grid>
                      <Grid xs={12} md={6}>
                        <FormControl>
                          <FormLabel>Show Generation Date</FormLabel>
                          <Switch
                            checked={formData.showGenerationDate}
                            onChange={(e) => setFormData(prev => ({ ...prev, showGenerationDate: e.target.checked }))}
                          />
                        </FormControl>
                      </Grid>
                      <Grid xs={12} md={6}>
                        <FormControl>
                          <FormLabel>Show Page Numbers</FormLabel>
                          <Switch
                            checked={formData.showPageNumbers}
                            onChange={(e) => setFormData(prev => ({ ...prev, showPageNumbers: e.target.checked }))}
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Card>

                  {/* Typography Settings */}
                  <Card sx={{ p: 2, mb: 3 }}>
                    <Typography level="title-md" sx={{ mb: 2 }}>Typography</Typography>
                    <Grid container spacing={2}>
                      <Grid xs={12} md={6}>
                        <FormControl>
                          <FormLabel>Font Family</FormLabel>
                          <Select
                            value={formData.fontFamily}
                            onChange={(_, value) => setFormData(prev => ({ ...prev, fontFamily: value || 'Arial, sans-serif' }))}
                          >
                            <Option value="Poppins, Arial, sans-serif">Poppins</Option>
                            <Option value="Arial, sans-serif">Arial</Option>
                            <Option value="Times New Roman, serif">Times New Roman</Option>
                            <Option value="Helvetica, sans-serif">Helvetica</Option>
                            <Option value="Georgia, serif">Georgia</Option>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid xs={12} md={6}>
                        <FormControl>
                          <FormLabel>Base Font Size: {formData.fontSizeBase}px</FormLabel>
                          <Slider
                            value={formData.fontSizeBase}
                            onChange={(_, value) => setFormData(prev => ({ ...prev, fontSizeBase: value as number }))}
                            min={10}
                            max={20}
                            step={1}
                            valueLabelDisplay="auto"
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Card>
                </Box>
              </TabPanel>

                            {/* Document Templates Tab */}
              <TabPanel value={1}>
                {!selectedOrgId ? (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography level="body-md" sx={{ color: 'neutral.500' }}>
                      Please select an organization first to edit document templates.
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ p: 3 }}>
                    <Typography level="title-md" sx={{ mb: 2 }}>
                      Document Template Editor - {selectedDocType ? selectedDocType.charAt(0).toUpperCase() + selectedDocType.slice(1) : 'Document'}
                    </Typography>
                    <Typography level="body-sm" sx={{ mb: 3, color: 'neutral.500' }}>
                      Edit the HTML template for your {selectedDocType || 'document'}s. Use Handlebars variables like {'{{companyName}}'} for dynamic content.
                    </Typography>
                    
                    <Box sx={{ height: '500px', border: '1px solid', borderColor: 'divider', borderRadius: 'sm' }}>
                      <TinyMCEEditor
                        tinymceScriptSrc={'/tinymce/tinymce.min.js'}
                        licenseKey={'gpl'}
                        value={templateContent[(selectedDocType || 'Quotation') as keyof typeof templateContent] || ''}
                        onEditorChange={(content: string) => setTemplateContent(prev => ({ ...prev, [selectedDocType || 'Quotation']: content }))}
                        init={{
                          height: '100%',
                          menubar: true,
                          plugins: [
                            'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                            'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                            'insertdatetime', 'media', 'table', 'code', 'help', 'wordcount'
                          ],
                          toolbar: 'undo redo | blocks | ' +
                            'bold italic forecolor | alignleft aligncenter ' +
                            'alignright alignjustify | bullist numlist outdent indent | ' +
                            'removeformat | code | help',
                          content_style: `@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap'); body { font-family: 'Poppins', Arial, sans-serif; font-size:14px }`,
                          base_url: '/tinymce',
                          suffix: '.min',
                          font_family_formats:
                            "Poppins='Poppins', Arial, sans-serif;" +
                            "Arial=Arial, Helvetica, sans-serif;" +
                            "Helvetica=Helvetica, Arial, sans-serif;" +
                            "Times New Roman='Times New Roman', Times, serif;" +
                            "Georgia=Georgia, serif;",
                        }}
                      />
                    </Box>

                    <Box sx={{ mt: 2 }}>
                      <Typography level="title-sm" sx={{ mb: 1 }}>Available Template Variables:</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {[
                          // Company/customer
                          'companyName', 'companyAddress', 'companyPhone', 'companyEmail', 'companyVatNumber', 'companyWebsite',
                          'customerName', 'customerFullName', 'customerTitle', 'customerAddress', 'customerEmail', 'customerPhone',
                          // Document meta
                          'documentTitle', 'documentType', 'documentTypeLower', 'documentArticle', 'documentRef', 'documentDate', 'invoiceNumber', 'dueDate', 'paymentTermsLabel', 'paymentTermsWording',
                          // Content/lines
                          'description', 'lineItems', 'showVatColumn',
                          // Totals
                          'baseCostTotal', 'vatAmount', 'vatPct', 'vatBreakdown', 'totalExVat', 'totalIncVat', 'materialsInclVat', 'materialsExcVat', 'labourInclVat', 'labourExcVat', 'grandTotal', 'discountTotal', 'shippingTotal',
                          // Labour Duration
                          'labourDays', 'labourHours', 'labourDaysText', 'labourHoursText',
                          // Flags (use as condition blocks)
                          'vatEnabled', 'vatInclusiveDocument',
                          // Deposit and payments
                          'hasDeposit', 'isDepositInvoice', 'isBalanceInvoice', 'isVariationInvoice', 'depositNarrative', 'depositClearedFundsNote', 'quotedGross', 'worksSummary',
                          'depositInvoiceReference', 'depositAlreadyInvoicedNarrative', 'depositInvoiceAmount', 'depositInvoiceRef',
                          'depositAmount', 'depositPercentage', 'depositAppliedToInvoice', 'depositAppliedAmount', 'depositRemaining', 'depositReference',
                          'otherPaymentsAppliedToInvoice', 'otherPaymentsTotal', 'balanceDue',
                          // Stage-based invoice export
                          'stageOnly', 'paymentStructurePattern', 'contractTotalInclVat', 'stageLabel', 'stagePct', 'stageAmount', 'stageExVat', 'stageVatAmount', 'previouslyInvoicedInclVat', 'remainingAfterThisStageInclVat',
                          // Payment details
                          'paymentInstructions', 'bankName', 'bankAccountName', 'bankAccountNumber', 'bankSortCode'
                        ].map(variable => (
                          <Chip
                            key={variable}
                            size="sm"
                            variant="outlined"
                            onClick={() => {
                              // Insert variable into editor
                              const editor = (window as any).tinymce?.get(0);
                              if (editor) {
                                editor.insertContent(`{{${variable}}}`);
                              }
                            }}
                            sx={{ cursor: 'pointer' }}
                          >
                            {variable}
                          </Chip>
                        ))}
                      </Box>
                    </Box>
                  </Box>
                )}
              </TabPanel>

              {/* Preview Tab */}
              <TabPanel value={2}>
                {!selectedOrgId ? (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <Typography level="body-md" sx={{ color: 'neutral.500' }}>
                      Please select an organization first to preview documents.
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ p: 3 }}>
                    <Typography level="title-md" sx={{ mb: 2 }}>Document Preview</Typography>
                    <Typography level="body-sm" sx={{ mb: 3, color: 'neutral.500' }}>
                      Preview how your {selectedDocType || 'document'} will look with the current configuration.
                    </Typography>
                    
                    <Button
                      variant="soft"
                      startDecorator={<IconWrapper icon={FaEye} />}
                      onClick={openPreview}
                      sx={{ mb: 2 }}
                    >
                      Open Preview in New Window
                    </Button>
                    
                    <Box sx={{ 
                      border: '1px solid', 
                      borderColor: 'divider', 
                      borderRadius: 'sm', 
                      p: 2,
                      backgroundColor: 'background.surface',
                      minHeight: '400px'
                    }}>
                      <div dangerouslySetInnerHTML={{ __html: generatePreviewHtml() }} />
                    </Box>
                  </Box>
                )}
              </TabPanel>
            </Tabs>
          </Card>

          {/* Actions */}
          <Card sx={{ p: 2, mt: 3 }}>
            <Typography level="title-md" sx={{ mb: 2 }}>Actions</Typography>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <Button
                variant="soft"
                startDecorator={<IconWrapper icon={FaEye} />}
                onClick={openPreview}
              >
                Preview
              </Button>

              <Button
                variant="outlined"
                startDecorator={<IconWrapper icon={FaUndo} />}
                onClick={handleReset}
                disabled={!currentConfig}
              >
                Reset Changes
              </Button>
              <Button
                variant="solid"
                color="primary"
                startDecorator={<IconWrapper icon={FaSave} />}
                onClick={handleSave}
                loading={saving}
              >
                Save Configuration
              </Button>
            </Stack>
          </Card>

          {/* Configuration Status */}
          <Card sx={{ p: 2 }}>
            <Typography level="title-md" sx={{ mb: 2 }}>Configuration Status</Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <Chip
                color={currentConfig ? 'success' : 'warning'}
                variant="soft"
              >
                {currentConfig ? 'Configured' : 'Not Configured'}
              </Chip>
              {currentConfig && (
                <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                  Last updated: {new Date(currentConfig.updatedAt).toLocaleDateString('en-GB')}
                </Typography>
              )}
            </Stack>
          </Card>
        </>
      )}
    </Box>
  );
};

export default PdfExportConfigPanel;
